import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../config";

//Add new asyncThunk for bulk‐add at the top:
export const bulkAddGroupMembers = createAsyncThunk(
  "groupMembers/bulkAdd",
  async (bulkPayload, { rejectWithValue }) => {
    try {
      const { token } = JSON.parse(localStorage.getItem("userInfo"));
      const { data } = await axios.post(
        `${API_BASE_URL}/api/Communication/UserGroupMember/bulk`,
        bulkPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data; // { successfullyAddedMembers: [...], errors: [...] }
    } catch (error) {
      return rejectWithValue(
        error.response?.data ||
          error.message ||
          "Failed to bulk add group members"
      );
    }
  }
);

// ✅ UPDATED: GET Group Members by GroupId
export const fetchGroupMembers = createAsyncThunk(
  "groupMembers/fetchAll",
  async (groupId, { rejectWithValue }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      const { token } = JSON.parse(storedUser);

      const response = await axios.get(
        `${API_BASE_URL}/api/Communication/UserGroupMember/Group/${groupId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch group members"
      );
    }
  }
);

// ✅ Unchanged: POST Add New Group Member
export const addGroupMember = createAsyncThunk(
  "groupMembers/add",
  async (memberData, { rejectWithValue }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      const { token } = JSON.parse(storedUser);

      const response = await axios.post(
        `${API_BASE_URL}/api/Communication/UserGroupMember`,
        memberData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to add group member"
      );
    }
  }
);

// ✅ UPDATED: PUT Update Group Member Permissions
export const updateGroupMember = createAsyncThunk(
  "groupMembers/update",
  async ({ id, updatedData }, { rejectWithValue }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      const { token } = JSON.parse(storedUser);

      const response = await axios.put(
        `${API_BASE_URL}/api/Communication/UserGroupMember/${id}`,
        updatedData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to update group member"
      );
    }
  }
);

// ✅ UPDATED: DELETE Remove Group Member
export const removeGroupMember = createAsyncThunk(
  "groupMembers/remove",
  async (id, { rejectWithValue }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      const { token } = JSON.parse(storedUser);

      const response = await axios.delete(
        `${API_BASE_URL}/api/Communication/UserGroupMember/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return id; // Return the ID of the removed member to update the state
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to remove group member"
      );
    }
  }
);

// ✅ Redux Slice
const groupMembersSlice = createSlice({
  name: "groupMembers",
  initialState: {
    members: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // 🗃 Bulk Add Group Members
      .addCase(bulkAddGroupMembers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkAddGroupMembers.fulfilled, (state, action) => {
        state.loading = false;
        const { successfullyAddedMembers, errors } = action.payload;

        // Append all successful members
        state.members.push(...successfullyAddedMembers);

        // Stringify any per‐user errors into a single message
        if (errors?.length) {
          state.error = errors
            .map(
              (e) =>
                `User ${e.userId} in Group ${e.userGroupId}: ${e.errorMessage}`
            )
            .join("; ");
        } else {
          state.error = null;
        }
      })
      .addCase(bulkAddGroupMembers.rejected, (state, action) => {
        state.loading = false;

        // If payload is array of errors, stringify; otherwise use its message
        if (Array.isArray(action.payload)) {
          state.error = action.payload
            .map(
              (e) =>
                `User ${e.userId} in Group ${e.userGroupId}: ${e.errorMessage}`
            )
            .join("; ");
        } else {
          state.error = action.payload || action.error.message;
        }
      })

      // 🔁 Fetch Group Members
      .addCase(fetchGroupMembers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroupMembers.fulfilled, (state, action) => {
        state.loading = false;
        state.members = action.payload;
      })
      .addCase(fetchGroupMembers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ➕ Add Group Member
      .addCase(addGroupMember.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addGroupMember.fulfilled, (state, action) => {
        state.loading = false;
        state.members.push(action.payload);
      })
      .addCase(addGroupMember.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // 🔄 Update Group Member
      .addCase(updateGroupMember.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGroupMember.fulfilled, (state, action) => {
        state.loading = false;
        const updatedMember = action.payload;
        const index = state.members.findIndex(
          (member) => member.id === updatedMember.id
        );
        if (index !== -1) {
          state.members[index] = updatedMember;
        }
      })
      .addCase(updateGroupMember.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ❌ Remove Group Member
      .addCase(removeGroupMember.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeGroupMember.fulfilled, (state, action) => {
        state.loading = false;
        state.members = state.members.filter(
          (member) => member.id !== action.payload
        );
      })
      .addCase(removeGroupMember.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default groupMembersSlice.reducer;
